import React, { createContext, useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { matchPath, useLocation } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import LocationIcon from '@mui/icons-material/PinDrop';
import CloseIcon from '@mui/icons-material/Close';
import setUserRealm from 'redux/actions/userRealm';
import Styles from 'global/styles';
import { Access, getRealmIcon } from 'global/components/droprealms';
import { getFirstKey } from 'global/utils';
import { get, set } from 'global/lib/lodash';
import { NAV_LOCATION_BANNER_HEIGHT_VAR } from 'global/components/FlexibleLayout/constants';
import {
  NavLocationBanner,
  NavLocationBannerReset,
  NavLocationBannerText,
  NavLocationButton,
  NavLocationIcon,
  NavResetButton,
  NavRealmModal,
} from './NavBar/NavBar.styles';
import { Row } from 'global/components/row';
import { setPushRealmToNative } from 'global/utils/nativeShell';

export const REALM_ADD_LISTENERS = 'REALM_ADD_LISTENERS';
export const APP_ACTIONS = 'APP_ACTIONS';
window[REALM_ADD_LISTENERS] = {};
window[APP_ACTIONS] = window[APP_ACTIONS] || {};

function shouldHideLocationBanner(pathname) {
  if (pathname.startsWith('/account') || pathname === '/profile') return true;
  if (pathname.startsWith('/courses')) return true;
  if (matchPath(pathname, { path: '/tasks/list/:id' })) return true;
  if (matchPath(pathname, { path: '/library/:library/document/:document' })) return true;
  return false;
}

const NavRealmContext = createContext(null);

function useNavRealmContext() {
  const ctx = useContext(NavRealmContext);
  if (!ctx) {
    throw new Error('NavRealm components must be used within NavRealmProvider');
  }
  return ctx;
}

export function NavRealmProvider(props) {
  const styles = Styles();
  const access = useRef();
  const pendingSel = useRef({});

  const realm = useSelector((state) => state.auth.realm);
  const dispatch = useDispatch();

  const buildButtons = (editing = false) => [
    {
      title: 'Clear',
      variant: 'text',
      disabled: editing,
      onClick: () => {
        pendingSel.current = {};
        get(access, 'current.clearSelection', () => 0)();
      },
    },
    {
      title: 'Apply',
      variant: 'contained',
      disabled: editing,
      onClick: () => {
        const next = getFirstKey(pendingSel.current);
        closeDialog(next || null);
      },
    },
  ];

  const [realmDialog, setRealmDialog] = useState({
    open: false,
    title: 'Set Location',
    keepMounted: true,
    maxWidth: '800px',
    onClose: () => setRealmDialog((d) => ({ ...d, open: false })),
    dialogActions: {
      style: { justifyContent: 'flex-end' },
    },
    buttons: buildButtons(false),
  });

  const handleClickOpen = () => {
    pendingSel.current = realm?.id ? { [realm.id]: realm } : {};
    setRealmDialog({
      ...realmDialog,
      open: true,
      buttons: buildButtons(false),
    });
  };

  const handleReset = (clear = false) => {
    if (clear) {
      dispatch(setUserRealm(null));
      pendingSel.current = {};
      setRealmDialog({
        ...realmDialog,
        buttons: buildButtons(false),
      });
    }
    get(access, 'current.reset', () => 0)();
  };

  const closeDialog = (value) => {
    if (value && value.id) {
      dispatch(setUserRealm(value));
    } else if (value === null) {
      dispatch(setUserRealm(null));
    }
    setRealmDialog({
      ...realmDialog,
      open: false,
    });
  };

  const onEditSuccess = (e) => {
    try {
      const listeners = window[REALM_ADD_LISTENERS] || {};
      Object.keys(listeners).map((k) => listeners[k](e));
    } catch (err) {
      console.warn(err);
    }
  };

  set(window, [APP_ACTIONS, 'setRealm'], (r) => {
    try {
      dispatch(setUserRealm(r));
    } catch (err) {
      console.error(err);
    }
  });

  useEffect(() => {
    setPushRealmToNative(() => {
      get(window, 'TLM.setNativeRealm', () => 0)(realm || null);
    });
    return () => setPushRealmToNative(null);
  }, [realm]);

  return (
    <NavRealmContext.Provider value={{ realm, handleClickOpen, handleReset }}>
      {props.children}
      <NavRealmModal {...realmDialog} fullHeight={true}>
        <div className={styles.formContainer}>
          <Access
            ref={access}
            openToSelected={true}
            canAdd
            open={get(realmDialog, 'open')}
            currentRealm={realm}
            singleSelect={true}
            disableListScroll
            onSelectedChange={(s) => {
              pendingSel.current = s || {};
            }}
            onEditingChange={(editing) => {
              setRealmDialog((d) => ({ ...d, buttons: buildButtons(editing) }));
            }}
            onEditSuccess={onEditSuccess}
          />
        </div>
      </NavRealmModal>
    </NavRealmContext.Provider>
  );
}

export function NavRealmControl() {
  const { realm, handleClickOpen, handleReset } = useNavRealmContext();

  return (
    <Row>
      <NavLocationButton
        variant="contained"
        color="primary"
        data-testid="navbar-button-location-set"
        data-location-set={!!realm}
        disableElevation
        onClick={handleClickOpen}
        aria-label={realm ? `Location: ${realm.name}` : 'Set location'}
      >
        <span className="nav-location-label">{realm ? realm.name : <em>set location</em>}</span>
        <NavLocationIcon>{realm ? getRealmIcon(realm) : <LocationIcon />}</NavLocationIcon>
      </NavLocationButton>

      {/* Hidden below lg via CSS; banner owns reset on compact widths. */}
      {realm && (
        <NavResetButton variant="contained" color="primary" aria-label="reset location" data-testid="navbar-button-location-reset" onClick={() => handleReset(true)}>
          <CloseIcon />
        </NavResetButton>
      )}
    </Row>
  );
}

const clearLocationBannerHeightVar = () => {
  document.documentElement.style.removeProperty(NAV_LOCATION_BANNER_HEIGHT_VAR);
};

const publishLocationBannerHeight = (el) => {
  if (!el) return;
  // display:none (lg+) yields offsetHeight 0, so the overlay keeps its rest top.
  document.documentElement.style.setProperty(NAV_LOCATION_BANNER_HEIGHT_VAR, `${el.offsetHeight}px`);
};

export function NavRealmBanner() {
  const { realm, handleClickOpen, handleReset } = useNavRealmContext();
  const { pathname } = useLocation();
  const theme = useTheme();
  const bannerRef = useRef(null);
  const showBanner = !!realm && !shouldHideLocationBanner(pathname);

  // Publish height so PrintView's overlay sits below this banner between md and lg.
  // ResizeObserver covers content/zoom; matchMedia covers the CSS display toggle at lg
  // without listening to every window resize.
  useLayoutEffect(() => {
    if (!showBanner) {
      clearLocationBannerHeightVar();
      return undefined;
    }

    const el = bannerRef.current;
    if (!el) return undefined;

    const publish = () => publishLocationBannerHeight(el);
    publish();
    const observer = new ResizeObserver(publish);
    observer.observe(el);
    const mq = window.matchMedia(theme.breakpoints.down('lg').replace(/^@media\s*/, ''));
    mq.addEventListener('change', publish);

    return () => {
      observer.disconnect();
      mq.removeEventListener('change', publish);
      clearLocationBannerHeightVar();
    };
  }, [showBanner, theme]);

  if (!showBanner) return null;

  return (
    <NavLocationBanner ref={bannerRef} data-testid="navbar-location-banner">
      <LocationIcon fontSize="small" />
      <NavLocationBannerText onClick={handleClickOpen} role="button" tabIndex={0} onKeyDown={(e) => e.key === 'Enter' && handleClickOpen()}>
        Your location is set to {realm.name}
      </NavLocationBannerText>
      <NavLocationBannerReset aria-label="reset location" onClick={() => handleReset(true)} size="small">
        <CloseIcon fontSize="small" />
      </NavLocationBannerReset>
    </NavLocationBanner>
  );
}
